import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { Phase } from '@chess/api-model';
import type { ScaleKind } from './scale.ts';
import { useOpenAndReturn } from '@chess/api-navigation/react';
import { registerCard } from '@chess/api-dashboard';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import '@chess/api-insights/charts.css';
import './stage-scatter.css';
import { StageScatterCard, stageScatterTitle } from './StageScatterCard.tsx';
import { StageScatterAggregator } from './StageScatterAggregator.ts';

// "<Stage> swings" (Opening, Middlegame and Ending swings): one card, registered once per stage with its
// own aggregator and its title (`registerCard`: the section draws the
// frame), among the stage's top cards (`home.section.<stage>`, which
// Home's stage section and the stage's screen both show), third in each: the
// opening's (order 30, after Most played and Losing openings), the
// middlegame's (order 30, after Middlegame blunders and misses) and the
// ending's (order 7). A dot opens
// its game's review at the stage's first move; back returns to the dot
// (api-navigation).
// The opening's offsets cluster within a few points of zero, so its vertical
// scale is fitted to the games' own swings (scale.ts, 2026-10-08; a symmetric log for the opening and linear for the others before).
const PLACES: Array<{ stage: Phase; slot: string; order: number; scale: ScaleKind }> = [
  { stage: 'opening', slot: 'home.section.openings', order: 30, scale: 'fitted' },
  { stage: 'middlegame', slot: 'home.section.middlegame', order: 30, scale: 'fitted' },
  { stage: 'ending', slot: 'home.section.ending', order: 7, scale: 'fitted' },
];

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  for (const { stage, slot, order, scale } of PLACES) {
    const aggregator = new StageScatterAggregator(stage);
    registerAggregator(getApi, aggregator);
    const Card: React.FC = () => {
      const { result, state } = useAggregate(getApi, aggregator);
      const openAndReturn = useOpenAndReturn();
      // Never hidden while the store loads: the card shows skeletons for its values.
      if (state?.status !== 'ready') return state?.status === 'failed' ? null : <StageScatterCard stage={stage} loading />;
      const p = state.period;
      return result && p ? <StageScatterCard stage={stage} scale={scale} {...result} from={p.from} to={p.to} onOpen={openAndReturn} /> : null;
    };
    registerCard(app, slot, Card, { order, title: stageScatterTitle(stage) });
  }
}
